/* eslint-disable max-lines -- Deep TipTap composer facade: keeps editor lifecycle, draft extraction, and inline suggestion arbitration behind one Projects-facing interface. */
'use client';

import { isEditorLive } from '@/components/tiptap/shared/safe-editor';
import { renderPlaceholderSlots } from '@/components/ui/composer-placeholder-slots';
import {
  filterShortcuts,
  type SearchShortcut,
} from '@/components/ui/floating-ask-heidi/shortcut-search';
import { ChatObjectTokenHoverCard } from '@/composition/hosts/chat-object-hover-card';
import { useIsWorkSessionArtifactV2Enabled } from '@/features/flags';
import { useComposerEvidenceShortcuts } from '@/features/projects/hooks/useComposerEvidenceShortcuts';
import {
  artifactForLinkedItem,
  useLinkableContextItems,
  type UseLinkableContextItemsArgs,
} from '@/features/projects/hooks/useLinkableContextItems';
import { useIsProjectRecordingAvailable } from '@/features/projects/projectRecordingAvailability';
import { useReferenceableLibraryDocuments } from '@/features/workflows/components/useWorkflowContextDocumentUpload';
import { useQueryDocumentTemplates } from '@/hooks/query/useQueryDocumentTemplates';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { track } from '@/lib/analytics/track';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  trackWorkHomeComposerCta,
  trackWorkHomeComposerViewed,
} from '@/models/analytics/features/trackWorkHomeComposerAnalytics';
import type { WorkSurface } from '@/models/analytics/features/work';
import { isComposingEvent } from '@/utils/keyboardEventHelpers';
import {
  ChatObjectToken,
  ComposerContextMention,
  hasChatObjectToken,
  orderObjectMentionItems,
  ProjectsStarterBundleNode,
  SpotlightSlashAnchoredBelowPanel,
  SpotlightSlashPickerPanel,
  type ProjectsComposerPromptEditorHandle,
} from '@heidi/agents';
import type { LinkedContextItem, WorkflowSkill } from '@heidi/agents/contracts';
import { WORK_HOME_COMPOSER_ENTRY } from '@heidi/agents/contracts';
import type { Artifact } from '@heidi/agents/data';
import {
  buildSpotlightSlashSections,
  chatObjectPreviewTarget,
  chatObjectTokenAttrsForLinkedItem,
  createProjectsContextSuggestion,
  createProjectsSkillSuggestion,
  EMPTY_PROJECTS_COMPOSER_PROMPT_DRAFT,
  flattenSpotlightSections,
  isGenericSessionArtifact,
  isSpotlightActionItem,
  isSpotlightEvidenceShortcutItem,
  isSpotlightWorkflowItem,
  MAKE_AS_WORKFLOW_COMPOSER_PREFILL,
  normalizeDraftForMode,
  objectPreviewArtifact,
  peekArtifactAtom,
  projectsComposerDraftToTiptapDoc,
  projectsComposerTextToTiptapDoc,
  stripComposerInvisibleChars,
  tiptapDocToProjectsComposerDraft,
  useSpotlightSlashPanelLayout,
  workflowSkillKind,
  type ChatObjectTokenAttrs,
  type ProjectsComposerPromptDraft,
  type ProjectsComposerSkillReference,
  type ProjectsContextSuggestionSession,
  type ProjectsSkillSuggestionSession,
  type SpotlightActionItem,
  type SpotlightSlashItem,
  type SpotlightSlashShortcut,
} from '@heidi/agents/data';
import { typographyVariant } from '@heidi/design-system/typography';
import { useCollections } from '@heidi/evidence/data';
import { ClinicalTaskStatus } from '@heidi/schema/ml-scribe';
import type { ClinicalTask } from '@heidi/tasks/contracts';
import { useQueryClinicalTasks } from '@heidi/tasks/data';
import { isFormTemplateCategory } from '@heidi/templates/data';
import { useLatestRef } from '@heidi/utils';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import History from '@tiptap/extension-history';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import type { SuggestionKeyDownProps } from '@tiptap/suggestion';
import { useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ProjectsSkillReferenceNode } from './projects-composer-skill-node';

const projectsComposerSkillPrefillKey = (skill: ProjectsComposerSkillReference): string =>
  JSON.stringify({
    skillId: skill.skillId,
    recordingId: skill.recordingId ?? null,
    slug: skill.slug,
    title: skill.title,
    kind: skill.kind ?? null,
  });

/** Evidence's ranking of its own catalogue, mapped field by field for the "/" menu. */
const rankEvidenceShortcuts = (
  shortcuts: readonly SearchShortcut[],
  query: string
): SpotlightSlashShortcut[] =>
  filterShortcuts([...shortcuts], query).map((shortcut) => ({
    id: shortcut.id,
    label: shortcut.label,
    query: shortcut.query,
    description: shortcut.description,
  }));

const contextItemMessages = defineMessages({
  untitledTemplate: {
    defaultMessage: 'Untitled template',
    id: 'gqeBcmpodT',
    description: 'Fallback name for a note template without a title',
  },
});

const templateContextItem = (
  template: { id: string; name?: string | null; template_category?: string | null },
  untitled: string
): LinkedContextItem => ({
  type: 'template',
  templateId: template.id,
  label: template.name?.trim() || untitled,
  ...(isFormTemplateCategory(template.template_category) ? { formTemplate: true } : {}),
});

// Work Home's exact params, so the picker reads the cache entries Home already holds.
const OPEN_TODO_TASKS_PARAMS = {
  status: ClinicalTaskStatus.todo,
  sortBy: 'created_at',
  sortOrder: 'desc',
  limit: 100,
} as const;
const OPEN_IN_PROGRESS_TASKS_PARAMS = {
  ...OPEN_TODO_TASKS_PARAMS,
  status: ClinicalTaskStatus.in_progress,
} as const;

const taskContextItems = (tasks: readonly ClinicalTask[]): LinkedContextItem[] => {
  const seen = new Set<string>();
  return tasks.flatMap((task): LinkedContextItem[] => {
    const label = (task.title ?? task.short_title ?? '').trim();
    if (!label || seen.has(task.id)) return [];
    seen.add(task.id);
    const patientName = task.patient_name?.trim();
    const dueDate = task.due_date?.trim();
    return [
      {
        type: 'task',
        taskId: task.id,
        label,
        ...(task.session_id ? { sessionId: task.session_id } : {}),
        ...(patientName ? { patientName } : {}),
        ...(dueDate ? { dueDate } : {}),
        ...(task.status === ClinicalTaskStatus.todo ||
        task.status === ClinicalTaskStatus.in_progress
          ? { status: task.status }
          : {}),
      },
    ];
  });
};

const collectionContextItem = (collection: {
  collection_id: string;
  name: string;
  document_count: number;
}): LinkedContextItem[] => {
  const label = collection.name.trim();
  return label
    ? [
        {
          type: 'collection',
          collectionId: collection.collection_id,
          label,
          documentCount: collection.document_count,
        },
      ]
    : [];
};

interface ObjectMentionSourceItems {
  readonly tasks: readonly LinkedContextItem[];
  readonly collections: readonly LinkedContextItem[];
}

const NO_OBJECT_MENTION_SOURCE_ITEMS: ObjectMentionSourceItems = {
  tasks: [],
  collections: [],
};

// Mounted only with object tokens, so a flag-off editor reads no auth and holds no query.
function ObjectMentionSources({
  includeTasks,
  onItems,
}: {
  includeTasks: boolean;
  onItems: (items: ObjectMentionSourceItems) => void;
}): null {
  const { isAuthenticated } = useIsAuthenticated();
  const tasksEnabled = includeTasks && isAuthenticated;
  const { data: todoTasks } = useQueryClinicalTasks(OPEN_TODO_TASKS_PARAMS, {
    enabled: tasksEnabled,
  });
  const { data: inProgressTasks } = useQueryClinicalTasks(OPEN_IN_PROGRESS_TASKS_PARAMS, {
    enabled: tasksEnabled,
  });
  const { data: collections } = useCollections();
  // A disabled query still hands back what Work Home cached.
  const tasks = useMemo(
    () =>
      tasksEnabled
        ? taskContextItems([
            ...(todoTasks?.tasks ?? []),
            ...(inProgressTasks?.tasks ?? []),
          ])
        : [],
    [tasksEnabled, todoTasks?.tasks, inProgressTasks?.tasks]
  );
  const collectionItems = useMemo(
    () => (collections?.collections ?? []).flatMap(collectionContextItem),
    [collections?.collections]
  );
  useEffect(() => {
    onItems({ tasks, collections: collectionItems });
  }, [onItems, tasks, collectionItems]);
  return null;
}

const recordActionMessages = defineMessages({
  label: {
    defaultMessage: 'Record routine',
    id: '2hVueMHR/A',
    description: 'Slash action label for recording a new reusable routine',
  },
  prompt: {
    defaultMessage: 'Record a new routine.',
    id: 'qdqb7TA4ay',
    description: 'Prompt inserted after selecting the Project chat record action',
  },
});

const createRoutineActionMessages = defineMessages({
  label: {
    defaultMessage: 'Create routine',
    id: 'gmsYpa2PWz',
    description:
      'Slash action label for turning the current chat into a reusable routine',
  },
});

export interface ProjectsComposerPromptEditorProps {
  readonly availableSkills: readonly WorkflowSkill[];
  readonly placeholder: string;
  /**
   * Transient override for the VISIBLE placeholder only (Evidence starter hover
   * preview). Deliberately separate from `placeholder`, which names the textbox:
   * an accessible name that followed the pointer would rewrite itself on
   * mouse-move for screen-reader users.
   */
  readonly placeholderPreview?: string | null;
  /** Visible-only node rendered in the same overlay box as the starter preview. */
  readonly placeholderOverlay?: ReactNode;
  readonly disabled?: boolean;
  readonly prefilledValue?: string;
  readonly prefilledSkill?: ProjectsComposerSkillReference;
  readonly onDraftChange: (draft: ProjectsComposerPromptDraft) => void;
  readonly onSubmitRequest: (draft: ProjectsComposerPromptDraft) => void;
  /**
   * Sends a picked Evidence shortcut as its own turn. The slash query is
   * already gone, so the typed draft stays. Absent: the question lands in the box.
   */
  readonly onShortcutSend?: (query: string) => void;
  readonly onUnhandledKeyDown?: (event: KeyboardEvent) => boolean;
  readonly onLinkContext?: (item: LinkedContextItem) => void;
  readonly onOpenArtifact?: (artifact: Artifact) => void;
  /**
   * True: `@` inserts an inline object token.
   * False (default): main's `@` pick — link a chip, open its canvas.
   */
  readonly objectTokens?: boolean;
  readonly onFocus?: () => void;
  readonly onBlur?: () => void;
  /**
   * True while either autocomplete menu owns the input — the `@` context picker
   * or the `/` spotlight. A host that draws its own panel under the composer
   * uses this to stand down rather than stack two menus.
   */
  readonly onPickerOpenChange?: (open: boolean) => void;
  /**
   * TipTap paste interceptor for clipboard file attachments.
   * Return ``true`` when files were staged so TipTap skips inserting
   * binary paste as text.
   */
  readonly onClipboardPaste?: (event: ClipboardEvent) => boolean;
  /** Notifies parent when the TipTap editor instance is created or destroyed. */
  readonly onEditorChange?: (editor: Editor | null) => void;
  readonly includeCreateWorkflow?: boolean;
  /** Additional context gate; local native/release availability is always required. */
  readonly includeRecordAction?: boolean;
  readonly slashSectionOrder?: 'routines-first' | 'shortcuts-first';
  /** Override the element used to position the portaled slash picker. */
  readonly pickerAnchorRef?: RefObject<HTMLElement | null>;
  readonly pickerPlacement?: 'above' | 'below';
  /** Focus the editor once on mount when enabled (Work home / project chat entry). */
  readonly autoFocus?: boolean;
  readonly className?: string;
  readonly editorClassName?: string;
  readonly testId?: string;
  /** When set, slash/@ panel interactions emit Work home analytics. */
  readonly analyticsSurface?: WorkSurface;
  /** Narrows `@` sessions to one patient and drops patients (in-session chats). */
  readonly patientScope?: UseLinkableContextItemsArgs['patientScope'];
}

const ComposerShortcutMarketplace = dynamic(
  () =>
    import('@/components/ui/floating-ask-heidi/composer-shortcut-marketplace').then(
      (mod) => mod.ComposerShortcutMarketplace
    ),
  { ssr: false, loading: () => null }
);

export const ProjectsComposerPromptEditor = forwardRef<
  ProjectsComposerPromptEditorHandle,
  ProjectsComposerPromptEditorProps
>(function ProjectsComposerPromptEditor(
  {
    availableSkills,
    placeholder,
    placeholderPreview,
    placeholderOverlay,
    disabled = false,
    prefilledValue,
    prefilledSkill,
    onDraftChange,
    onSubmitRequest,
    onShortcutSend,
    onUnhandledKeyDown,
    onLinkContext,
    onOpenArtifact,
    objectTokens = false,
    onFocus,
    onBlur,
    onPickerOpenChange,
    onClipboardPaste,
    onEditorChange,
    includeCreateWorkflow = false,
    includeRecordAction = false,
    slashSectionOrder,
    autoFocus = false,
    pickerAnchorRef: pickerAnchorRefProp,
    pickerPlacement = 'above',
    className,
    editorClassName,
    testId = 'projects-landing-composer-input',
    analyticsSurface,
    patientScope,
  },
  ref
) {
  const intl = useIntl();
  const isProjectRecordingAvailable = useIsProjectRecordingAvailable();
  // Both slash actions resolve to the same `replaceEditorText(editor, prompt)`,
  // so the prompt travels on the item and the editor dispatch is uniform —
  // no per-actionId branch. `actionId` stays for tracking and picker keys.
  const spotlightActions = useMemo<readonly SpotlightActionItem[]>(() => {
    const actions: SpotlightActionItem[] = [];
    if (includeCreateWorkflow) {
      actions.push({
        kind: 'action',
        actionId: 'create-routine',
        label: intl.formatMessage(createRoutineActionMessages.label),
        slug: 'create-routine',
        prompt: MAKE_AS_WORKFLOW_COMPOSER_PREFILL,
      });
    }
    if (includeRecordAction && isProjectRecordingAvailable) {
      actions.push({
        kind: 'action',
        actionId: 'record',
        label: intl.formatMessage(recordActionMessages.label),
        prompt: intl.formatMessage(recordActionMessages.prompt),
        slug: 'record',
      });
    }
    return actions;
  }, [includeCreateWorkflow, includeRecordAction, isProjectRecordingAvailable, intl]);
  const editorRef = useRef<Editor | null>(null);
  const internalPickerAnchorRef = useRef<HTMLDivElement | null>(null);
  const pickerAnchorRef = pickerAnchorRefProp ?? internalPickerAnchorRef;
  const SlashPanel =
    pickerPlacement === 'below'
      ? SpotlightSlashAnchoredBelowPanel
      : SpotlightSlashPickerPanel;
  const didAutoFocusRef = useRef(false);
  const latestDraftRef = useRef<ProjectsComposerPromptDraft>(
    EMPTY_PROJECTS_COMPOSER_PROMPT_DRAFT
  );
  const lastAppliedPrefillRef = useRef<string | undefined>(undefined);
  const appendedPrefillRangeRef = useRef<{
    readonly editor: Editor;
    readonly from: number;
    readonly to: number;
    readonly text: string;
  } | null>(null);
  const lastAppliedSkillRef = useRef<{ editor: Editor; key: string } | null>(null);
  const onDraftChangeRef = useRef(onDraftChange);
  const onSubmitRequestRef = useRef(onSubmitRequest);
  const onShortcutSendRef = useRef(onShortcutSend);
  onShortcutSendRef.current = onShortcutSend;
  const onUnhandledKeyDownRef = useRef(onUnhandledKeyDown);
  const onClipboardPasteRef = useRef(onClipboardPaste);
  const onEditorChangeRef = useRef(onEditorChange);
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  const placeholderRef = useRef(placeholder);
  const skillsRef = useRef<readonly WorkflowSkill[]>(availableSkills);
  const evidenceShortcutsState = useComposerEvidenceShortcuts();
  const evidenceShortcutsEnabled = evidenceShortcutsState.enabled;
  const evidenceShortcuts = evidenceShortcutsState.shortcuts;
  const evidenceShortcutsRef = useRef(evidenceShortcutsState);
  const linkableContext = useLinkableContextItems({ limit: 50, patientScope });
  const linkableContextRef = useRef(linkableContext);
  const libraryDocuments = useReferenceableLibraryDocuments({ limit: 50 });
  const libraryDocumentsRef = useRef(libraryDocuments);
  const { data: documentTemplates } = useQueryDocumentTemplates(objectTokens);
  const templateItems = useMemo(() => {
    const untitled = intl.formatMessage(contextItemMessages.untitledTemplate);
    return (documentTemplates ?? []).map((template) =>
      templateContextItem(template, untitled)
    );
  }, [documentTemplates, intl]);
  const templateItemsRef = useLatestRef(templateItems);
  const slashSectionOrderRef = useLatestRef(slashSectionOrder);
  const objectMentionSourceItemsRef = useRef(NO_OBJECT_MENTION_SOURCE_ITEMS);
  const setObjectMentionSourceItems = useCallback((items: ObjectMentionSourceItems) => {
    objectMentionSourceItemsRef.current = items;
  }, []);
  const spotlightActionsRef = useRef(spotlightActions);
  const onLinkContextRef = useRef(onLinkContext);
  const onOpenArtifactRef = useRef(onOpenArtifact);
  const isWorkSessionArtifactV2Enabled = useIsWorkSessionArtifactV2Enabled();
  const isWorkSessionArtifactV2EnabledRef = useRef(isWorkSessionArtifactV2Enabled);
  const objectTokensRef = useLatestRef(objectTokens);
  const hasFocusedRef = useRef(false);
  const peekArtifact = useSetAtom(peekArtifactAtom);
  const openObjectRef = useLatestRef((attrs: ChatObjectTokenAttrs) => {
    const target = chatObjectPreviewTarget(attrs);
    if (target) peekArtifact(objectPreviewArtifact(target));
  });
  const [contextSession, setContextSession] =
    useState<ProjectsContextSuggestionSession | null>(null);
  const mentionLayout = useSpotlightSlashPanelLayout(
    pickerAnchorRef,
    pickerPlacement,
    contextSession !== null,
    pickerPlacement === 'below'
  );
  const contextSessionRef = useRef<ProjectsContextSuggestionSession | null>(null);
  const [contextActiveIndex, setContextActiveIndexState] = useState(0);
  const contextActiveIndexRef = useRef(0);
  const contextKeyDownRef = useRef<((props: SuggestionKeyDownProps) => boolean) | null>(
    null
  );
  const contextListboxId = useId();
  const contextOptionId = useCallback(
    (index: number) => `${contextListboxId}-context-opt-${index}`,
    [contextListboxId]
  );
  const [skillSession, setSkillSession] = useState<ProjectsSkillSuggestionSession | null>(
    null
  );
  const [shortcutMarketplaceOpen, setShortcutMarketplaceOpen] = useState(false);
  const skillSessionRef = useRef<ProjectsSkillSuggestionSession | null>(null);
  const [skillActiveIndex, setSkillActiveIndexState] = useState(0);
  const skillActiveIndexRef = useRef(0);
  const skillKeyDownRef = useRef<((props: SuggestionKeyDownProps) => boolean) | null>(
    null
  );
  const skillListboxId = useId();
  const skillOptionId = useCallback(
    (index: number) => `${skillListboxId}-skill-opt-${index}`,
    [skillListboxId]
  );
  const spotlightSections = useMemo(() => {
    if (!skillSession) return [];
    return buildSpotlightSlashSections(
      skillSession.query,
      { skills: skillsRef.current },
      {
        actions: spotlightActions,
        filterEvidenceShortcuts: evidenceShortcutsEnabled
          ? (filter) => rankEvidenceShortcuts(evidenceShortcuts, filter)
          : undefined,
        sectionOrder: slashSectionOrder,
      }
    );
  }, [
    skillSession,
    spotlightActions,
    evidenceShortcutsEnabled,
    evidenceShortcuts,
    slashSectionOrder,
  ]);
  const spotlightFlatItems = useMemo(
    () => flattenSpotlightSections(spotlightSections),
    [spotlightSections]
  );

  useEffect(() => {
    onDraftChangeRef.current = onDraftChange;
  }, [onDraftChange]);

  useEffect(() => {
    onSubmitRequestRef.current = onSubmitRequest;
  }, [onSubmitRequest]);

  useEffect(() => {
    onUnhandledKeyDownRef.current = onUnhandledKeyDown;
  }, [onUnhandledKeyDown]);

  useEffect(() => {
    onClipboardPasteRef.current = onClipboardPaste;
  }, [onClipboardPaste]);

  useEffect(() => {
    onEditorChangeRef.current = onEditorChange;
  }, [onEditorChange]);

  useEffect(() => {
    onFocusRef.current = onFocus;
  }, [onFocus]);

  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  useEffect(() => {
    onLinkContextRef.current = onLinkContext;
  }, [onLinkContext]);

  useEffect(() => {
    onOpenArtifactRef.current = onOpenArtifact;
  }, [onOpenArtifact]);

  useEffect(() => {
    isWorkSessionArtifactV2EnabledRef.current = isWorkSessionArtifactV2Enabled;
  }, [isWorkSessionArtifactV2Enabled]);

  useEffect(() => {
    placeholderRef.current = placeholder;
  }, [placeholder]);

  useEffect(() => {
    skillsRef.current = availableSkills;
  }, [availableSkills]);

  useEffect(() => {
    evidenceShortcutsRef.current = evidenceShortcutsState;
  }, [evidenceShortcutsState]);

  useEffect(() => {
    linkableContextRef.current = linkableContext;
  }, [linkableContext]);

  useEffect(() => {
    libraryDocumentsRef.current = libraryDocuments;
  }, [libraryDocuments]);

  useEffect(() => {
    spotlightActionsRef.current = spotlightActions;
  }, [spotlightActions]);

  const onPickerOpenChangeRef = useRef(onPickerOpenChange);
  useEffect(() => {
    onPickerOpenChangeRef.current = onPickerOpenChange;
  }, [onPickerOpenChange]);

  // One signal for both menus: a host suppressing its own panel does not care
  // which of the two took the input.
  const isPickerOpen = contextSession !== null || skillSession !== null;
  useEffect(() => {
    onPickerOpenChangeRef.current?.(isPickerOpen);
  }, [isPickerOpen]);

  const setContextActiveIndex = useCallback((index: number) => {
    contextActiveIndexRef.current = index;
    setContextActiveIndexState(index);
  }, []);

  const handleContextSessionChange = useCallback(
    (next: ProjectsContextSuggestionSession | null) => {
      if (analyticsSurface && next && !contextSessionRef.current) {
        trackWorkHomeComposerViewed(WORK_HOME_COMPOSER_ENTRY.mentionPanelOpen, {
          surface: analyticsSurface,
        });
      }
      contextSessionRef.current = next;
      setContextSession(next);
      setContextActiveIndex(0);
    },
    [analyticsSurface, setContextActiveIndex]
  );

  const linkContextItem = useCallback((item: LinkedContextItem) => {
    onLinkContextRef.current?.(item);
    const artifact = artifactForLinkedItem(item);
    if (!artifact) return;
    // Under the focused-pane flag a whole-session artifact means "go to the
    // Session page". Picking one here only attaches it as context.
    if (isWorkSessionArtifactV2EnabledRef.current && isGenericSessionArtifact(artifact)) {
      return;
    }
    onOpenArtifactRef.current?.(artifact);
  }, []);

  const handleContextSelect = useCallback(
    (item: LinkedContextItem) => {
      if (analyticsSurface) {
        trackWorkHomeComposerCta(WORK_HOME_COMPOSER_ENTRY.mentionPanelPick(item.type), {
          surface: analyticsSurface,
        });
      }
      if (objectTokensRef.current) return;
      linkContextItem(item);
      editorRef.current?.commands.focus('end');
    },
    [analyticsSurface, linkContextItem, objectTokensRef]
  );

  const setSkillActiveIndex = useCallback((index: number) => {
    skillActiveIndexRef.current = index;
    setSkillActiveIndexState(index);
  }, []);

  const handleSkillSessionChange = useCallback(
    (next: ProjectsSkillSuggestionSession | null) => {
      if (analyticsSurface && next && !skillSessionRef.current) {
        trackWorkHomeComposerViewed(WORK_HOME_COMPOSER_ENTRY.slashPanelOpen, {
          surface: analyticsSurface,
        });
      }
      skillSessionRef.current = next;
      setSkillSession(next);
      setSkillActiveIndex(0);
    },
    [analyticsSurface, setSkillActiveIndex]
  );

  useEffect(() => {
    if (!skillSession) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (target?.closest('[data-testid="spotlight-slash-picker-panel"]')) return;
      if (target?.closest('.ProseMirror')) return;

      skillSessionRef.current?.dismiss();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [skillSession]);

  const handleContextKeyDown = useCallback(
    (event: KeyboardEvent): boolean => {
      const session = contextSessionRef.current;
      if (!session) return false;

      if (event.key === 'Escape') {
        event.preventDefault();
        session.dismiss();
        return true;
      }

      if (session.items.length === 0) return false;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setContextActiveIndex((contextActiveIndexRef.current + 1) % session.items.length);
        return true;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setContextActiveIndex(
          (contextActiveIndexRef.current - 1 + session.items.length) %
            session.items.length
        );
        return true;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const choice = session.items[contextActiveIndexRef.current] ?? session.items[0];
        if (choice) session.select(choice);
        return true;
      }

      return false;
    },
    [setContextActiveIndex]
  );

  contextKeyDownRef.current = ({ event }) => handleContextKeyDown(event);

  const handleSkillKeyDown = useCallback(
    (event: KeyboardEvent): boolean => {
      const session = skillSessionRef.current;
      if (!session) return false;

      if (event.key === 'Escape') {
        event.preventDefault();
        session.dismiss();
        return true;
      }

      if (session.items.length === 0) return false;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSkillActiveIndex((skillActiveIndexRef.current + 1) % session.items.length);
        return true;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSkillActiveIndex(
          (skillActiveIndexRef.current - 1 + session.items.length) % session.items.length
        );
        return true;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const choice = session.items[skillActiveIndexRef.current] ?? session.items[0];
        if (choice) session.select(choice);
        return true;
      }

      return false;
    },
    [setSkillActiveIndex]
  );

  skillKeyDownRef.current = ({ event }) => handleSkillKeyDown(event);

  const publishDraft = useCallback((editor: Editor) => {
    const draft = tiptapDocToProjectsComposerDraft(editor.getJSON());
    latestDraftRef.current = draft;
    onDraftChangeRef.current(draft);
    return draft;
  }, []);

  const trackSlashPanelPick = useCallback(
    (kind: string) => {
      if (!analyticsSurface) return;
      trackWorkHomeComposerCta(WORK_HOME_COMPOSER_ENTRY.slashPanelPick(kind), {
        surface: analyticsSurface,
      });
    },
    [analyticsSurface]
  );

  const replaceEditorText = useCallback(
    (editor: Editor, text: string) => {
      editor.commands.setContent(projectsComposerTextToTiptapDoc(text));
      publishDraft(editor);
      const mentionAt = text.indexOf('@');
      // A slot in the middle of the sentence has to leave the caret on the
      // trigger, or the mention picker stays closed. A trailing `@` is already
      // where `focus('end')` lands.
      if (mentionAt >= 0 && mentionAt < text.length - 1 && !text.includes('\n')) {
        editor
          .chain()
          .focus()
          .setTextSelection(mentionAt + 2)
          .run();
      } else {
        editor.commands.focus('end');
      }
      // Whichever branch ran, the selection command alone leaves DOM focus where
      // the click put it, and the composer's suggestion sheet only renders while
      // the editor holds it — so a starter prefill would land as text with no
      // typeahead behind it. Same pairing this component's own `focus()` handle does.
      editor.view.focus();
    },
    [publishDraft]
  );

  const handleSpotlightSelect = useCallback(
    (item: SpotlightSlashItem) => {
      if (isSpotlightActionItem(item)) {
        trackSlashPanelPick(item.actionId);
        const editor = editorRef.current;
        if (editor) replaceEditorText(editor, item.prompt);
        return;
      }
      if (isSpotlightEvidenceShortcutItem(item)) {
        trackSlashPanelPick('evidence_shortcut');
        const editor = editorRef.current;
        if (!editor) return;
        track({
          event_name: TrackingEventName.EvidenceShortcutRun,
          props: {
            shortcut_source: 'composer',
            via: 'click',
            shortcut_id: item.id,
            query_length: item.query.length,
          },
          options: { amplitude: false },
        });
        const send = onShortcutSendRef.current;
        if (send) {
          send(item.query);
          return;
        }
        replaceEditorText(editor, item.query);
        return;
      }
      if (isSpotlightWorkflowItem(item)) {
        trackSlashPanelPick('workflow');
        const editor = editorRef.current;
        const skill = item.skill;
        const kind = workflowSkillKind(skill);
        editor
          ?.chain()
          .focus('end')
          .insertProjectsSkillReference({
            skillId: skill.skillId,
            ...(skill.recordingId ? { recordingId: skill.recordingId } : {}),
            slug: skill.slug,
            title: skill.title,
            ...(kind ? { kind } : {}),
          })
          .run();
        if (editor) publishDraft(editor);
      }
    },
    [publishDraft, replaceEditorText, trackSlashPanelPick]
  );

  const handleBrowseShortcuts = useCallback(() => {
    skillSessionRef.current?.deleteQueryAndRun(() => {
      trackSlashPanelPick('browse_shortcuts');
      setShortcutMarketplaceOpen(true);
    });
  }, [trackSlashPanelPick]);

  const insertEditorSkill = useCallback(
    (editor: Editor, skill: ProjectsComposerSkillReference) => {
      editor
        .chain()
        .focus('end')
        .insertProjectsSkillReference({
          skillId: skill.skillId,
          ...(skill.recordingId ? { recordingId: skill.recordingId } : {}),
          slug: skill.slug,
          title: skill.title,
          ...(skill.kind ? { kind: skill.kind } : {}),
        })
        .run();
      editor.view.focus();
      publishDraft(editor);
    },
    [publishDraft]
  );

  const extensions = useMemo(
    () => [
      Document,
      Paragraph,
      Text,
      HardBreak,
      History,
      Placeholder.configure({
        // The starter preview is NOT routed through here: a CSS pseudo-element
        // can only carry flat text, and its `[slot]` segments have to render as
        // badges. It draws as an overlay below instead.
        placeholder: () => placeholderRef.current,
      }),
      ProjectsSkillReferenceNode,
      ProjectsStarterBundleNode,
      ChatObjectToken.configure({
        wrapToken: (attrs, token) => (
          <ChatObjectTokenHoverCard attrs={attrs}>{token}</ChatObjectTokenHoverCard>
        ),
        openObject: (attrs) => openObjectRef.current(attrs),
      }),
      createProjectsContextSuggestion({
        getItems: () => {
          const { sessionItems, patientItems } = linkableContextRef.current;
          const items = [
            ...sessionItems,
            ...patientItems,
            ...libraryDocumentsRef.current,
          ];
          return objectTokensRef.current
            ? orderObjectMentionItems([
                ...items,
                ...objectMentionSourceItemsRef.current.tasks,
                ...objectMentionSourceItemsRef.current.collections,
                ...templateItemsRef.current,
              ])
            : items;
        },
        onSelect: handleContextSelect,
        onSessionChange: handleContextSessionChange,
        keyDownRef: contextKeyDownRef,
        insertsToken: () => objectTokensRef.current,
      }),
      createProjectsSkillSuggestion({
        getItems: (query) =>
          flattenSpotlightSections(
            buildSpotlightSlashSections(
              query,
              { skills: skillsRef.current },
              {
                actions: spotlightActionsRef.current,
                filterEvidenceShortcuts: evidenceShortcutsRef.current.enabled
                  ? (filter) =>
                      rankEvidenceShortcuts(
                        evidenceShortcutsRef.current.shortcuts,
                        filter
                      )
                  : undefined,
                sectionOrder: slashSectionOrderRef.current,
              }
            )
          ),
        onSelect: handleSpotlightSelect,
        onSessionChange: handleSkillSessionChange,
        keyDownRef: skillKeyDownRef,
      }),
    ],
    [
      handleContextSelect,
      handleContextSessionChange,
      handleSkillSessionChange,
      handleSpotlightSelect,
      objectTokensRef,
      openObjectRef,
      slashSectionOrderRef,
      templateItemsRef,
    ]
  );

  const editor = useEditor({
    extensions,
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    editable: !disabled,
    editorProps: {
      attributes: {
        'aria-label': placeholder,
        'aria-autocomplete': 'list',
        role: 'textbox',
        'data-testid': testId,
        class: cn(
          // No min-h: content height drives the line box so the caret and the
          // absolute placeholder share the same vertical origin (see also
          // floating-ask-heidi-input). Parent rows supply click-target height.
          'w-full whitespace-pre-wrap break-words text-sm leading-normal text-text-primary focus:outline-none',
          '[&_p]:m-0',
          editorClassName
        ),
      },
      handleDOMEvents: {
        focus: () => {
          hasFocusedRef.current = true;
          onFocusRef.current?.();
          return false;
        },
        blur: () => {
          onBlurRef.current?.();
          return false;
        },
      },
      // Paste files stage as attachments; returning true suppresses TipTap's
      // default (which would otherwise insert binary clipboard data as text).
      handlePaste: (_view, event) => onClipboardPasteRef.current?.(event) ?? false,
      handleKeyDown: (_view, event): boolean => {
        if (handleContextKeyDown(event)) return true;
        if (handleSkillKeyDown(event)) return true;
        if (onUnhandledKeyDownRef.current?.(event)) return true;
        if (
          isComposingEvent(event) ||
          event.key !== 'Enter' ||
          event.shiftKey ||
          event.metaKey ||
          event.ctrlKey
        ) {
          return false;
        }
        event.preventDefault();
        onSubmitRequestRef.current(latestDraftRef.current);
        return true;
      },
    },
    onCreate: ({ editor: created }) => {
      editorRef.current = created;
      publishDraft(created);
      onEditorChangeRef.current?.(created);
    },
    onDestroy: () => {
      editorRef.current = null;
      onEditorChangeRef.current?.(null);
    },
    onUpdate: ({ editor: updated }) => {
      publishDraft(updated);
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (
      !autoFocus ||
      !isEditorLive(editor, 'ProjectsComposerPromptEditor.autoFocus') ||
      disabled ||
      didAutoFocusRef.current
    ) {
      return;
    }
    didAutoFocusRef.current = true;
    queueMicrotask(() => {
      if (!isEditorLive(editor, 'ProjectsComposerPromptEditor.autoFocus.microtask'))
        return;
      editor.commands.focus('end');
      editor.view.focus();
    });
  }, [autoFocus, disabled, editor]);

  useEffect(() => {
    if (!isEditorLive(editor, 'ProjectsComposerPromptEditor.placeholder')) return;
    const { state } = editor.view;
    if (!state) return;
    editor.view.dispatch(state.tr);
  }, [editor, placeholder]);

  useEffect(() => {
    if (!isEditorLive(editor, 'ProjectsComposerPromptEditor.prefill')) return;
    if (prefilledValue === undefined || prefilledValue === '') {
      // Only a seed this effect applied. A typed draft has no applied prefill.
      if (lastAppliedPrefillRef.current) {
        const appended = appendedPrefillRangeRef.current;
        const appendedTextIsIntact =
          appended?.editor === editor &&
          appended.to <= editor.state.doc.content.size &&
          editor.state.doc.textBetween(appended.from, appended.to, '', '') ===
            appended.text;
        if (appended && appendedTextIsIntact) {
          editor.chain().focus('end').deleteRange(appended).run();
          publishDraft(editor);
        } else if (!appended) {
          editor.commands.clearContent(true);
        }
        // Once the clinician edits inside the seeded range, that text is theirs.
        // A withdrawn seed must not delete or rewrite any part of it.
      }
      appendedPrefillRangeRef.current = null;
      lastAppliedPrefillRef.current = prefilledValue;
      return;
    }
    if (lastAppliedPrefillRef.current === prefilledValue) return;
    lastAppliedPrefillRef.current = prefilledValue;
    const seed = stripComposerInvisibleChars(prefilledValue);
    const currentDraft = tiptapDocToProjectsComposerDraft(editor.getJSON());
    const hasObjectToken = currentDraft.segments.some(
      (segment) => segment.kind === 'object'
    );
    if (!hasObjectToken) {
      appendedPrefillRangeRef.current = null;
      replaceEditorText(editor, prefilledValue);
      return;
    }

    const previous = appendedPrefillRangeRef.current;
    const previousIsIntact =
      previous?.editor === editor &&
      previous.to <= editor.state.doc.content.size &&
      editor.state.doc.textBetween(previous.from, previous.to, '', '') === previous.text;
    const trailingSegment = currentDraft.segments.at(-1);
    const hasTrailingContent = currentDraft.segments.length > 0;
    const endsInWhitespace =
      trailingSegment?.kind === 'text' && /\s$/.test(trailingSegment.text);
    const separator = previousIsIntact
      ? previous.text.startsWith(' ')
        ? ' '
        : ''
      : hasTrailingContent && !endsInWhitespace
        ? ' '
        : '';
    const insertedText = `${separator}${seed}`;
    if (previous && previousIsIntact) {
      editor
        .chain()
        .focus('end')
        .insertContentAt({ from: previous.from, to: previous.to }, insertedText)
        .run();
    } else {
      editor.chain().focus('end').insertContent(insertedText).run();
    }
    publishDraft(editor);

    const to = editor.state.selection.to;
    const from = to - insertedText.length;
    appendedPrefillRangeRef.current = { editor, from, to, text: insertedText };
    const mentionAt = seed.indexOf('@');
    if (mentionAt >= 0 && mentionAt < seed.length - 1 && !seed.includes('\n')) {
      editor
        .chain()
        .focus()
        .setTextSelection(from + separator.length + mentionAt + 1)
        .run();
    } else {
      editor.commands.focus('end');
    }
    editor.view.focus();
  }, [editor, prefilledValue, publishDraft, replaceEditorText]);

  useEffect(() => {
    if (!isEditorLive(editor, 'ProjectsComposerPromptEditor.prefilledSkill')) return;
    if (!prefilledSkill) {
      lastAppliedSkillRef.current = null;
      return;
    }
    const nextKey = projectsComposerSkillPrefillKey(prefilledSkill);
    if (
      lastAppliedSkillRef.current?.editor === editor &&
      lastAppliedSkillRef.current.key === nextKey
    ) {
      return;
    }
    lastAppliedSkillRef.current = { editor, key: nextKey };
    editor.commands.setContent(projectsComposerTextToTiptapDoc(''));
    insertEditorSkill(editor, prefilledSkill);
  }, [editor, insertEditorSkill, prefilledSkill]);

  useEffect(() => {
    if (!isEditorLive(editor, 'ProjectsComposerPromptEditor.listbox')) return;
    const element = editor.view.dom;
    const activeListboxId = contextSession
      ? contextListboxId
      : skillSession
        ? skillListboxId
        : undefined;
    const activeOptionId =
      contextSession && contextSession.items.length > 0
        ? contextOptionId(contextActiveIndex)
        : skillSession && spotlightFlatItems.length > 0
          ? skillOptionId(skillActiveIndex)
          : undefined;

    if (activeListboxId) {
      element.setAttribute('aria-expanded', 'true');
      element.setAttribute('aria-controls', activeListboxId);
    } else {
      element.setAttribute('aria-expanded', 'false');
      element.removeAttribute('aria-controls');
    }

    if (activeOptionId) {
      element.setAttribute('aria-activedescendant', activeOptionId);
    } else {
      element.removeAttribute('aria-activedescendant');
    }
  }, [
    contextActiveIndex,
    contextListboxId,
    contextOptionId,
    contextSession,
    editor,
    skillActiveIndex,
    skillListboxId,
    skillOptionId,
    skillSession,
    spotlightFlatItems.length,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      focus: (position = 'end') => {
        editor?.commands.focus(position);
        editor?.view.focus();
      },
      getEditor: () => editorRef.current,
      openShortcutPicker: () => {
        const session: ProjectsSkillSuggestionSession = {
          query: '',
          items: [],
          select: (item) => {
            handleSpotlightSelect(item);
            handleSkillSessionChange(null);
          },
          deleteQueryAndRun: (callback) => {
            handleSkillSessionChange(null);
            callback();
          },
          dismiss: () => handleSkillSessionChange(null),
        };
        handleSkillSessionChange(session);
        editor?.commands.focus('end');
      },
      replaceText: (text) => {
        if (!editor) return;
        replaceEditorText(editor, text);
      },
      replaceDraft: (draft) => {
        if (!editor) return;
        editor.commands.setContent(
          projectsComposerDraftToTiptapDoc(
            normalizeDraftForMode(draft, objectTokensRef.current)
          )
        );
        publishDraft(editor);
        editor.commands.focus('end');
      },
      insertSkill: (skill) => {
        if (!editor) return;
        insertEditorSkill(editor, skill);
      },
      insertChatObject: (item) => {
        if (!isEditorLive(editor, 'ProjectsComposerPromptEditor.insertChatObject'))
          return;
        const attrs = chatObjectTokenAttrsForLinkedItem(item);
        if (hasChatObjectToken(editor.state.doc, attrs)) return;
        editor
          .chain()
          .focus(hasFocusedRef.current ? undefined : 'end', { scrollIntoView: false })
          .insertChatObjectToken(attrs)
          .run();
      },
      clear: () => {
        if (!editor) return;
        editor.commands.clearContent(true);
        latestDraftRef.current = EMPTY_PROJECTS_COMPOSER_PROMPT_DRAFT;
        onDraftChangeRef.current(EMPTY_PROJECTS_COMPOSER_PROMPT_DRAFT);
      },
    }),
    [
      editor,
      handleSkillSessionChange,
      handleSpotlightSelect,
      insertEditorSkill,
      objectTokensRef,
      publishDraft,
      replaceEditorText,
    ]
  );

  return (
    <div
      ref={pickerAnchorRefProp ? undefined : internalPickerAnchorRef}
      className={cn(
        'relative min-w-0 flex-1',
        // Leftover titles are one inline atom. Keep them in normal wrap flow so
        // the prompt grows down instead of scrolling sideways.
        '[&_[data-projects-starter-bundle]]:inline [&_[data-projects-starter-bundle]]:whitespace-normal [&_[data-projects-starter-bundle]]:break-words',
        // Absolute + truncate: the old float/h-0 TipTap placeholder wraps onto a
        // second visual line in narrow composers (routines + open artifact).
        // Position the empty `<p>` (not only ProseMirror) so `before:top-0` is
        // relative to the line box — padded editors (in-chat `p-1`) otherwise
        // pin the placeholder to the ProseMirror box while the caret sits lower.
        '[&_.ProseMirror]:relative',
        '[&_p.is-editor-empty]:relative',
        '[&_.is-editor-empty]:before:pointer-events-none',
        '[&_.is-editor-empty]:before:text-text-secondary',
        '[&_p.is-editor-empty]:before:absolute [&_p.is-editor-empty]:before:inset-x-0',
        '[&_p.is-editor-empty]:before:top-0',
        '[&_p.is-editor-empty]:before:truncate',
        '[&_p.is-editor-empty]:before:content-[attr(data-placeholder)]',
        // The preview stands in for the placeholder, so the flat one steps aside
        // rather than drawing underneath the badges.
        (placeholderOverlay != null || Boolean(placeholderPreview)) &&
          '[&_p.is-editor-empty]:before:invisible',
        // DS disabled colour for value and placeholder while locked, not an opacity fade.
        disabled &&
          'cursor-not-allowed [&_.ProseMirror]:!text-text-disabled [&_.is-editor-empty]:before:!text-text-disabled',
        className
      )}
      data-disabled={disabled ? 'true' : undefined}
    >
      <EditorContent editor={editor} />
      {objectTokens ? (
        <ObjectMentionSources
          includeTasks={!patientScope}
          onItems={setObjectMentionSourceItems}
        />
      ) : null}
      {placeholderOverlay != null || placeholderPreview ? (
        // Centered in the editor box so it shares the caret's line when the
        // ProseMirror is padded (compact home uses py-1). An unpadded box is
        // the same height as the line, so centering still sits on the placeholder.
        <span
          aria-hidden="true"
          data-testid={
            placeholderPreview ? 'projects-composer-placeholder-preview' : undefined
          }
          className="pointer-events-none absolute inset-0 flex items-center overflow-hidden"
        >
          {placeholderOverlay ?? (
            <span
              className={cn(
                typographyVariant({ variant: 'p2' }),
                'block w-full min-w-0 truncate',
                disabled ? 'text-text-disabled' : 'text-text-secondary'
              )}
            >
              {renderPlaceholderSlots(placeholderPreview ?? '')}
            </span>
          )}
        </span>
      ) : null}
      {contextSession ? (
        <ComposerContextMention
          session={contextSession}
          activeIndex={contextActiveIndex}
          listboxId={contextListboxId}
          optionId={contextOptionId}
          onHover={setContextActiveIndex}
          layout={mentionLayout}
          objectTokens={objectTokens}
        />
      ) : skillSession ? (
        // In-chat composer docks at the bottom of the transcript — open above
        // so workflows/actions aren't clipped off-screen. Home opens below.
        <SlashPanel
          anchorRef={pickerAnchorRef}
          sections={spotlightSections}
          flatItems={spotlightFlatItems}
          activeIndex={skillActiveIndex}
          onActiveIndexChange={setSkillActiveIndex}
          listboxId={skillListboxId}
          optionId={skillOptionId}
          onSelect={skillSession.select}
          onBrowseShortcuts={evidenceShortcutsEnabled ? handleBrowseShortcuts : undefined}
        />
      ) : null}
      {shortcutMarketplaceOpen ? (
        <ComposerShortcutMarketplace onOpenChange={setShortcutMarketplaceOpen} />
      ) : null}
    </div>
  );
});
